/**
 * React Query hooks for Social Lookup feature
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as socialApi from '@/services/socialLookupApi';
import type { SocialPlatform } from '@/types/socialLookup';

export const socialLookupKeys = {
  all: ['socialLookup'] as const,
  workerStatus: () => [...socialLookupKeys.all, 'workerStatus'] as const,
  placeStatus: (placeIds: string[]) => [...socialLookupKeys.all, 'placeStatus', placeIds] as const,
  completedIds: () => [...socialLookupKeys.all, 'completedIds'] as const,
};

export function useWorkerStatus() {
  return useQuery({
    queryKey: socialLookupKeys.workerStatus(),
    queryFn: socialApi.getWorkerStatus,
    staleTime: 5000,
    refetchInterval: 10000,
  });
}

export function usePlaceClickStatus(placeIds: string[]) {
  return useQuery({
    queryKey: socialLookupKeys.placeStatus(placeIds),
    queryFn: () => socialApi.getPlaceStatus(placeIds),
    enabled: placeIds.length > 0,
    staleTime: 5000,
  });
}

export function useCompletedPlaceIds() {
  return useQuery({
    queryKey: socialLookupKeys.completedIds(),
    queryFn: socialApi.getCompletedPlaceIds,
    staleTime: 30000,
  });
}

export function useRecordClick() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ placeId, platform }: { placeId: string; platform: SocialPlatform }) =>
      socialApi.recordClick(placeId, platform),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: socialLookupKeys.workerStatus() });
      queryClient.invalidateQueries({ queryKey: socialLookupKeys.all });
    },
  });
}
